import { doc, getDoc, setDoc, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { db, auth } from '../config/firebase';

export interface CompanySettings {
  companyName: string;
  companyLogo?: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
  };
  phone: string;
  fax: string;
  website: string;
  email: string;
  contactInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    title: string;
  };
}

const COLLECTION = 'company_settings';
const PRIVATE_COLLECTION = 'company_private';

class CompanySettingsService {
  private static instance: CompanySettingsService;
  private listeners: (() => void)[] = [];
  private cachedSettings: CompanySettings;
  private firestoreUnsubscribe: Unsubscribe | null = null;
  private currentOwnerId: string | null = null;
  private initialized = false;

  private defaultSettings: CompanySettings = {
    companyName: 'Your Practice Name',
    companyLogo: undefined,
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: ''
    },
    phone: '',
    fax: '',
    website: '',
    email: '',
    contactInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      title: ''
    }
  };

  constructor() {
    this.cachedSettings = { ...this.defaultSettings };
  }

  public static getInstance(): CompanySettingsService {
    if (!CompanySettingsService.instance) {
      CompanySettingsService.instance = new CompanySettingsService();
    }
    return CompanySettingsService.instance;
  }

  private getOwnerId(): string {
    // Prefer the explicitly set practice owner ID (set via startListening)
    // over auth.currentUser.uid, since team members need to read/write
    // the practice owner's settings, not their own.
    if (this.currentOwnerId) return this.currentOwnerId;
    const user = auth.currentUser;
    if (user) return user.uid;
    return '';
  }

  /**
   * Start real-time listener for company settings.
   * Uses the practice owner's UID as the document ID.
   */
  public startListening(ownerId?: string): void {
    const uid = ownerId || this.getOwnerId();
    if (!uid) return;

    // Don't re-subscribe if already listening for same owner
    if (this.currentOwnerId === uid && this.firestoreUnsubscribe) return;

    this.stopListening();
    this.currentOwnerId = uid;

    const docRef = doc(db, COLLECTION, uid);

    this.firestoreUnsubscribe = onSnapshot(docRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        this.cachedSettings = {
          ...this.defaultSettings,
          ...data,
          address: { ...this.defaultSettings.address, ...(data.address || {}) },
          contactInfo: { ...this.defaultSettings.contactInfo, ...(data.contactInfo || {}) },
        };
      } else {
        this.cachedSettings = { ...this.defaultSettings };
      }
      this.initialized = true;
      this.notifyListeners();
      // Dispatch event for components that listen via window events
      window.dispatchEvent(new CustomEvent('companySettingsUpdated'));
    }, (error) => {
      console.error('CompanySettingsService snapshot error:', error);
    });
  }

  public stopListening(): void {
    if (this.firestoreUnsubscribe) {
      this.firestoreUnsubscribe();
      this.firestoreUnsubscribe = null;
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }

  public subscribe(listener: () => void): void {
    this.listeners.push(listener);
  }

  public unsubscribe(listener: () => void): void {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  public getSettings(): CompanySettings {
    // Don't auto-init here — let ProviderDashboard control startListening()
    // with the correct practiceOwnerId. Auto-init caused team members
    // to load their own (empty) settings instead of the practice owner's.
    return { ...this.cachedSettings };
  }

  public async updateSettings(updatedSettings: Partial<CompanySettings>): Promise<void> {
    const uid = this.getOwnerId();
    if (!uid) {
      console.error('No authenticated user — cannot update company settings');
      return;
    }

    const newSettings = { ...this.cachedSettings, ...updatedSettings };

    // Handle nested objects properly
    if (updatedSettings.address) {
      newSettings.address = { ...this.cachedSettings.address, ...updatedSettings.address };
    }
    if (updatedSettings.contactInfo) {
      newSettings.contactInfo = { ...this.cachedSettings.contactInfo, ...updatedSettings.contactInfo };
    }

    try {
      await setDoc(doc(db, COLLECTION, uid), newSettings, { merge: true });
      // Cache updates immediately (Firestore listener will also fire)
      this.cachedSettings = newSettings;
      this.notifyListeners();
    } catch (error) {
      console.error('Error saving company settings to Firestore:', error);
    }
  }

  // The EIN lives outside company_settings, which any signed-in account can read.
  public async getEin(): Promise<string> {
    const uid = this.getOwnerId();
    if (!uid) return '';
    try {
      const snapshot = await getDoc(doc(db, PRIVATE_COLLECTION, uid));
      return snapshot.exists() ? (snapshot.data().ein || '') : '';
    } catch (error) {
      console.error('Error reading practice EIN:', error);
      return '';
    }
  }

  public async setEin(ein: string): Promise<void> {
    const uid = this.getOwnerId();
    if (!uid) throw new Error('No authenticated user — cannot save EIN');
    await setDoc(doc(db, PRIVATE_COLLECTION, uid), { ein }, { merge: true });
  }

  public updateCompanyLogo(logoBase64: string): void {
    this.updateSettings({ companyLogo: logoBase64 });
  }

  public getCompanyName(): string {
    return this.getSettings().companyName;
  }

  public getCompanyLogo(): string | undefined {
    return this.getSettings().companyLogo;
  }
}

export default CompanySettingsService;
